import React, { Children, Fragment, useState } from 'react';
import classnames from 'classnames';
import partition from 'lodash/partition';
import get from 'lodash/get';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Button } from 'formlabs-web-components/Link';
import { Header } from 'formlabs-web-components/Type';
import { DisplayComponent } from 'formlabs-web-components/BaseComponents';

import { setActiveColumn } from '../../ducks/specTable';
import useIsMobile from '../../hooks/useIsMobile';

import './SpecTable.module.css';
import { InteractiveDiv } from 'formlabs-web-components/Wrapper';

const createData = rows => {
  const columnCount = rows[0].items.length;
  return Array.from({ length: columnCount }, (v, i) => i).map(column => ({
    key: `col-${column}`,
    rows: rows.map(row => get(row, `items[${column}]`, { content: '' }))
  }));
};

const ColumnSwitch = ({ hiddenColumns, title, callback }) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <InteractiveDiv
      styleName={classnames('switch', { 'is-open': isOpen })}
      handler={() => {
        setOpen(!isOpen);
      }}
    >
      {title}{' '}
      <span styleName="icon-open">
        <i className="fl-navigate_next" />
      </span>
      {isOpen &&
        hiddenColumns.map(column => (
          <div
            key={`switch-${column.key}`}
            styleName="switch-item"
            handler={() => callback(column.key)}
          >
            {column.rows[0].content}
          </div>
        ))}
    </InteractiveDiv>
  );
};

const SpecTable = ({
  classes,
  title,
  subTitle,
  children,
  activeDynamicColumnState,
  setActiveDynamicColumn,
  tableGroup
}) => {
  const isSmall = useIsMobile(600);
  const isMedium = useIsMobile(800);

  if (!children) {
    return null;
  }

  // Massage the data passed as children plugins
  const tableChilds = Children.toArray(children);
  const content = tableChilds.map(row => ({
    ...row.props,
    children: Children.toArray(row.props.children).map(column => column.props)
  }));
  const rows = content.map(row => ({
    key: row.rowKey,
    title: row.title,
    items: row.children
  }));
  const data = createData(rows);

  if (!data.length) {
    return null;
  }

  const dataColumnsCount = data.length < 3 ? data.length : isSmall ? 2 : 3;
  const initiaActivelColumn =
    data.length < 3 ? data[0].key : isSmall ? data[1].key : data[2].key;
  const activeDynamicColumn = activeDynamicColumnState || initiaActivelColumn;
  const hasInlineTitle = isMedium || isSmall;
  const columnCount = dataColumnsCount + (hasInlineTitle ? 0 : 1);

  const withIndex = fn => {
    let index = 0;
    return entry => fn(entry, index++);
  };
  const [columns, hiddenColumns] = partition(
    data,
    withIndex((column, index) => {
      if (data.length <= dataColumnsCount) {
        return true;
      }

      if (index + 1 < dataColumnsCount) {
        return true;
      }

      if (index + 1 >= dataColumnsCount && column.key === activeDynamicColumn) {
        return true;
      }

      return false;
    })
  );

  return (
    <div styleName="spec-table" className={classes}>
      <div styleName="header">
        <Header displayAs={5} level={2}>
          {title}
        </Header>
        {subTitle ? (
          <div styleName="sub-title">
            <Button
              url={subTitle.url}
              color="light-blue"
              arrow="right"
              styles={{ padding: 0, margin: 0, fontSize: '1rem' }}
            >
              {subTitle.title}
            </Button>
          </div>
        ) : null}
      </div>
      <div
        styleName="grid"
        style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}
      >
        {rows.map((row, rowIndex) => {
          const isLastRow = rowIndex === rows.length - 1;

          return (
            <Fragment key={row.key}>
              {!hasInlineTitle ? (
                <div
                  styleName={classnames('row-title', { 'last-row': isLastRow })}
                >
                  {row.title}
                </div>
              ) : null}
              {columns.map((column, columnIndex) => {
                const hasColumnSwitcher =
                  hiddenColumns.length &&
                  rowIndex === 0 &&
                  columnIndex === columnCount - 1;

                return (
                  <div
                    key={`${row.key}-${column.key}`}
                    styleName={classnames('cell', {
                      'last-row': isLastRow,
                      'with-switch': hasColumnSwitcher
                    })}
                  >
                    {hasInlineTitle ? (
                      <div styleName="inline-title">{row.title}</div>
                    ) : null}

                    {column.rows[rowIndex].children ||
                      column.rows[rowIndex].content
                        .split('\n')
                        .map((line, index) => (
                          <Fragment
                            key={`paragraph-${row.key}-${column.key}-${index}`}
                          >
                            {line}
                            <br />
                          </Fragment>
                        )) ||
                      null}

                    {hasColumnSwitcher ? (
                      <div styleName="switch-wrapper">
                        <ColumnSwitch
                          hiddenColumns={hiddenColumns}
                          title={column.rows[rowIndex].content}
                          callback={columnKey => {
                            setActiveDynamicColumn(tableGroup, columnKey);
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const SpecRow = ({ children }) => children;
const SpecItem = () => null;

export { SpecRow, SpecItem };
export default compose(
  connect(
    (state, ownProps) => {
      return {
        activeDynamicColumnState: state.specTable[ownProps.tableGroup] || null
      };
    },
    dispatch => {
      return {
        setActiveDynamicColumn: (table, column) => {
          dispatch(setActiveColumn(table, column));
        }
      };
    }
  ),
  DisplayComponent
)(SpecTable);
